// ============================================================================
// KEYWORD MATCHER - Client-Side Only
// Extracts and matches keywords between resume and job description
// ============================================================================

/**
 * Common stop words to exclude
 */
const STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from',
  'has', 'he', 'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the',
  'to', 'was', 'will', 'with', 'have', 'this', 'they', 'we', 'you',
  'your', 'our', 'their', 'or', 'but', 'not', 'can', 'all', 'who',
  'been', 'were', 'what', 'when', 'where', 'which', 'while', 'how',
  'if', 'do', 'does', 'did', 'than', 'then', 'these', 'those', 'am',
  'an', 'any', 'each', 'few', 'more', 'most', 'other', 'some', 'such'
]);

/**
 * Normalize and tokenize text
 * @param {string} text - Input text
 * @returns {string[]} - Array of normalized tokens
 */
export const tokenize = (text) => {
  if (!text) return [];

  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .split(/\s+/)
    .filter(word => word.length > 2 && !STOP_WORDS.has(word));
};

/**
 * Extract n-grams (multi-word phrases)
 * @param {string} text - Input text
 * @param {number} n - Number of words in each n-gram
 * @returns {string[]} - Array of n-grams
 */
export const extractNGrams = (text, n = 2) => {
  const words = text.toLowerCase().split(/\s+/);
  const ngrams = [];

  for (let i = 0; i <= words.length - n; i++) {
    const ngram = words.slice(i, i + n).join(' ');
    // Only include if it contains at least one meaningful word
    if (!ngram.split(' ').every(word => STOP_WORDS.has(word))) {
      ngrams.push(ngram);
    }
  }

  return ngrams;
};

/**
 * Extract keywords from job description
 * @param {string} jobDescription - Job description text
 * @returns {Object} - Extracted keywords
 */
export const extractKeywords = (jobDescription) => {
  if (!jobDescription) {
    return {
      skills: [],
      technologies: [],
      allKeywords: [],
      bigrams: [],
      trigrams: []
    };
  }

  // Common technical skills and technologies
  const techPatterns = [
    // Programming languages
    'javascript', 'python', 'java', 'c\\+\\+', 'c#', 'ruby', 'php', 'swift', 'kotlin', 'go', 'rust', 'typescript',
    // Frameworks
    'react', 'angular', 'vue', 'node', 'express', 'django', 'flask', 'spring', 'rails', 'laravel',
    // Databases
    'sql', 'mysql', 'postgresql', 'mongodb', 'redis', 'oracle', 'dynamodb', 'cassandra',
    // Cloud
    'aws', 'azure', 'gcp', 'docker', 'kubernetes', 'terraform', 'jenkins',
    // Tools
    'git', 'jira', 'agile', 'scrum', 'ci/cd', 'rest', 'api', 'graphql',
    // Data
    'machine learning', 'data science', 'analytics', 'tableau', 'power bi', 'excel',
    // Soft skills
    'leadership', 'communication', 'teamwork', 'problem solving', 'analytical', 'creative'
  ];

  const lowerText = jobDescription.toLowerCase();
  const skills = [];
  const technologies = [];

  // Extract technical patterns
  techPatterns.forEach(pattern => {
    const regex = new RegExp(`\\b${pattern}\\b`, 'gi');
    if (regex.test(lowerText)) {
      const keyword = pattern.replace(/\\b/g, '').replace(/\\\+/g, '+');
      if (keyword.includes('learning') || keyword.includes('solving') || keyword.includes('bi')) {
        skills.push(keyword);
      } else {
        technologies.push(keyword);
      }
    }
  });

  // Extract single-word keywords (nouns, verbs)
  const tokens = tokenize(jobDescription);

  // Extract bigrams (2-word phrases)
  const bigrams = extractNGrams(jobDescription, 2);

  // Extract trigrams (3-word phrases)
  const trigrams = extractNGrams(jobDescription, 3);

  // Combine all keywords
  const allKeywords = [
    ...new Set([
      ...skills,
      ...technologies,
      ...tokens.filter(t => t.length > 3), // Longer single words
      ...bigrams.slice(0, 20), // Top bigrams
      ...trigrams.slice(0, 10)  // Top trigrams
    ])
  ];

  return {
    skills: [...new Set(skills)],
    technologies: [...new Set(technologies)],
    allKeywords,
    bigrams: [...new Set(bigrams)],
    trigrams: [...new Set(trigrams)]
  };
};

/**
 * Match keywords between resume and job description
 * @param {string} resumeText - Resume text
 * @param {Object} keywords - Extracted keywords from job description
 * @returns {Object} - Match results
 */
export const matchKeywords = (resumeText, keywords) => {
  if (!resumeText || !keywords) {
    return {
      matched: [],
      missing: [],
      matchPercentage: 0,
      matchedSkills: [],
      missingSkills: [],
      matchedTechnologies: [],
      missingTechnologies: []
    };
  }

  const lowerResume = resumeText.toLowerCase();

  // Match all keywords
  const matched = keywords.allKeywords.filter(keyword => {
    const regex = new RegExp(`\\b${keyword.replace(/\+/g, '\\+')}\\b`, 'i');
    return regex.test(lowerResume);
  });

  const missing = keywords.allKeywords.filter(keyword => {
    const regex = new RegExp(`\\b${keyword.replace(/\+/g, '\\+')}\\b`, 'i');
    return !regex.test(lowerResume);
  });

  // Match skills
  const matchedSkills = keywords.skills.filter(skill => {
    const regex = new RegExp(`\\b${skill.replace(/\+/g, '\\+')}\\b`, 'i');
    return regex.test(lowerResume);
  });

  const missingSkills = keywords.skills.filter(skill => {
    const regex = new RegExp(`\\b${skill.replace(/\+/g, '\\+')}\\b`, 'i');
    return !regex.test(lowerResume);
  });

  // Match technologies
  const matchedTechnologies = keywords.technologies.filter(tech => {
    const regex = new RegExp(`\\b${tech.replace(/\+/g, '\\+')}\\b`, 'i');
    return regex.test(lowerResume);
  });

  const missingTechnologies = keywords.technologies.filter(tech => {
    const regex = new RegExp(`\\b${tech.replace(/\+/g, '\\+')}\\b`, 'i');
    return !regex.test(lowerResume);
  });

  const matchPercentage = keywords.allKeywords.length > 0
    ? Math.round((matched.length / keywords.allKeywords.length) * 100)
    : 0;

  return {
    matched,
    missing: missing.slice(0, 15), // Limit to top 15 missing
    matchPercentage,
    matchedSkills,
    missingSkills,
    matchedTechnologies,
    missingTechnologies,
    totalKeywords: keywords.allKeywords.length,
    matchedCount: matched.length
  };
};

/**
 * Calculate keyword density for a specific keyword
 * @param {string} text - Text to analyze
 * @param {string} keyword - Keyword to find
 * @returns {number} - Density (0-1)
 */
export const calculateKeywordDensity = (text, keyword) => {
  if (!text || !keyword) return 0;

  const words = text.toLowerCase().split(/\s+/);
  const keywordLower = keyword.toLowerCase();
  const occurrences = words.filter(word => word === keywordLower).length;

  return words.length > 0 ? occurrences / words.length : 0;
};

/**
 * Get top keywords by frequency
 * @param {string} text - Text to analyze
 * @param {number} limit - Number of top keywords to return
 * @returns {Array} - Array of {keyword, count} objects
 */
export const getTopKeywords = (text, limit = 10) => {
  const tokens = tokenize(text);
  const frequency = {};

  tokens.forEach(token => {
    frequency[token] = (frequency[token] || 0) + 1;
  });

  return Object.entries(frequency)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([keyword, count]) => ({ keyword, count }));
};
